import type {
  LearnedAutoFillMergeItem,
  LearnedAutoFillPatternV1,
} from '../../../shared/lib/quickFill/learnedAutoFillLibrary';

export type QuickFillKeyLibraryDialogSource = 'table' | 'import' | 'manage';

export interface QuickFillKeyLibraryIndexedEntry {
  entry: LearnedAutoFillPatternV1;
  index: number;
  mergeItem?: LearnedAutoFillMergeItem;
}

export type QuickFillKeyLibrarySort =
  | 'library' | 'family' | 'requiredKeys' | 'objectType' | 'skey' | 'updatedAt';
export type QuickFillKeyLibrarySortDirection = 'ascending' | 'descending';
export type QuickFillKeyLibraryFilter = 'all' | 'usable' | 'shadowed';

interface BuildQuickFillKeyLibraryTableModelOptions {
  source: QuickFillKeyLibraryDialogSource;
  libraryEntries: readonly LearnedAutoFillPatternV1[];
  mergePlan: readonly LearnedAutoFillMergeItem[];
  query: string;
  page: number;
  rowsPerPage: number;
  sortBy?: QuickFillKeyLibrarySort;
  sortDirection?: QuickFillKeyLibrarySortDirection;
  filter?: QuickFillKeyLibraryFilter;
  builtInShadowedIds?: ReadonlySet<string>;
  frozenSortIndexes?: readonly number[];
}

export interface QuickFillKeyLibraryTableModel {
  indexedEntryCount: number;
  filteredEntryCount: number;
  visibleEntries: QuickFillKeyLibraryIndexedEntry[];
  visiblePage: number;
}

const searchableText = (entry: LearnedAutoFillPatternV1): string => [
  entry.sourceFamilyName,
  ...entry.requiredKeys,
  ...Object.values(entry.values),
].join(' ').toLocaleLowerCase();

const sortValue = (entry: LearnedAutoFillPatternV1, sortBy: QuickFillKeyLibrarySort): string => {
  if (sortBy === 'family') return entry.sourceFamilyName;
  if (sortBy === 'requiredKeys') return entry.requiredKeys.join(' ');
  if (sortBy === 'objectType') return entry.values.objectType;
  if (sortBy === 'skey') return entry.values.skey;
  if (sortBy === 'updatedAt') return entry.updatedAt;
  return '';
};

export const buildQuickFillKeyLibraryTableModel = ({
  source,
  libraryEntries,
  mergePlan,
  query,
  page,
  rowsPerPage,
  sortBy = 'library',
  sortDirection = 'ascending',
  filter = 'all',
  builtInShadowedIds = new Set(),
  frozenSortIndexes,
}: BuildQuickFillKeyLibraryTableModelOptions): QuickFillKeyLibraryTableModel => {
  const isManageMode = source === 'manage';
  const indexedEntries: QuickFillKeyLibraryIndexedEntry[] = isManageMode
    ? libraryEntries.map((entry, index) => ({ entry, index }))
    : mergePlan.map((mergeItem, index) => ({ entry: mergeItem.incoming, index, mergeItem }));
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredEntries = indexedEntries.filter(({ entry }) => {
    const matchesSearch = !normalizedQuery || searchableText(entry).includes(normalizedQuery);
    const isShadowed = builtInShadowedIds.has(entry.id);
    const matchesFilter = !isManageMode || filter === 'all' ||
      (filter === 'shadowed' ? isShadowed : !isShadowed);
    return matchesSearch && matchesFilter;
  });

  // Managed rows keep their storage order so an edit never moves the field under the cursor.
  if (!isManageMode) {
    filteredEntries.sort((left, right) =>
      left.entry.sourceFamilyName.localeCompare(right.entry.sourceFamilyName) ||
      left.entry.id.localeCompare(right.entry.id)
    );
  } else if (frozenSortIndexes) {
    const positions = new Map(frozenSortIndexes.map((index, position) => [index, position]));
    filteredEntries.sort((left, right) =>
      (positions.get(left.index) ?? Number.MAX_SAFE_INTEGER) -
      (positions.get(right.index) ?? Number.MAX_SAFE_INTEGER)
    );
  } else if (sortBy !== 'library') {
    const direction = sortDirection === 'ascending' ? 1 : -1;
    filteredEntries.sort((left, right) =>
      direction * sortValue(left.entry, sortBy).localeCompare(sortValue(right.entry, sortBy)) ||
      left.index - right.index
    );
  } else if (sortDirection === 'descending') {
    filteredEntries.reverse();
  }

  const lastPage = Math.max(0, Math.ceil(filteredEntries.length / rowsPerPage) - 1);
  const visiblePage = Math.min(page, lastPage);
  const pageStart = visiblePage * rowsPerPage;

  return {
    indexedEntryCount: indexedEntries.length,
    filteredEntryCount: filteredEntries.length,
    visibleEntries: filteredEntries.slice(pageStart, pageStart + rowsPerPage),
    visiblePage,
  };
};
